import { withErrno } from 'kerium';
import { _throw } from 'utilium/misc';
import { ArrayType } from './array.js';
import * as __field from './fields.internal.js';
import * as primitive from './primitives.js';
import { registerType } from './types.js';
/**
 * Polyfill Symbol.metadata
 * @see https://github.com/microsoft/TypeScript/issues/53461
 */
Symbol.metadata ??= Symbol.for('Symbol.metadata');
/**
 * Initializes the struct metadata for a class
 * This also handles copying metadata from parent classes
 */
function initMetadata(context) {
    context.metadata ??= {};
    const existing = context.metadata.structInit ?? {};
    context.metadata.structInit = {
        fields: [...(existing.fields ?? [])],
    };
    return context.metadata.structInit;
}
/**
 * Decorates a class as a struct.
 */
export function struct(...options) {
    const opts = options.reduce((acc, opt) => ({ ...acc, ...opt }), {});
    if (typeof this == 'object')
        Object.assign(opts, this);
    return function __decorateStruct(target, context) {
        const init = initMetadata(context);
        // Max alignment of all fields
        let fieldAlignment = 1;
        const fields = [];
        let size = 0;
        const align = (to) => {
            size = Math.ceil(size / to) * to;
        };
        for (const field of init.fields) {
            if (!opts.isPacked)
                align(field.alignment);
            if (opts.isUnion)
                size = Math.max(size, field.type.size);
            else {
                field.offset = size;
                size += field.type.size;
            }
            fields.push(field);
            fieldAlignment = Math.max(fieldAlignment, field.alignment);
        }
        opts.alignment ??= fieldAlignment;
        if (!opts.isPacked)
            align(opts.alignment);
        class _struct extends target {
            static name = target.name;
            static size = size;
            static alignment = opts.alignment;
            static isUnion = !!opts.isUnion;
            static isDynamic = !!opts.isDynamic;
            static fields = fields;
            static get(buffer, offset) {
                // @ts-expect-error 2511 : Please don't try to create an instance of an abstract struct
                return new this(buffer, offset);
            }
            static set(buffer, offset, value) {
                const source = new Uint8Array(value.buffer, value.byteOffset, this.size);
                const target = new Uint8Array(buffer, offset, this.size);
                if (value.buffer === buffer && value.byteOffset === offset)
                    return;
                for (let i = 0; i < this.size; i++)
                    target[i] = source[i];
            }
            static [Symbol.toStringTag] = `[struct ${target.name}]`;
            constructor(...args) {
                if (!args.length)
                    args = [new ArrayBuffer(size), 0, size];
                super(...args);
                if (!opts.fastFields)
                    Object.defineProperties(this, __field.descriptorsOf(this.constructor));
            }
        }
        __field.definePrototypeFields(_struct);
        context.addInitializer(function () {
            Object.defineProperty(_struct, 'name', { value: target.name });
            registerType(_struct);
        });
        return _struct;
    };
}
struct.packed = struct.bind({ isPacked: true });
struct.align = function (alignment) {
    return struct.bind({ alignment });
};
/**
 * Decorates a class as a union.
 */
export function union(options = {}) {
    return struct({ ...options, isUnion: true });
}
/**
 * Decorates a class member as a struct field.
 */
export function field(type, opt = {}) {
    return function __decorateField(value, context) {
        if (context.kind != 'accessor')
            throw withErrno('EINVAL', 'Field must be an accessor');
        const init = initMetadata(context);
        const field = __field.init(context.name, type, opt);
        init.fields.push(field);
        return {
            get() {
                return __field.get(this, field);
            },
            set(value) {
                __field.set(this, field, value);
            },
        };
    };
}
function _shortcut(typeName) {
    const type = primitive.types[primitive.normalize(typeName)];
    function __decoratePrimitiveField(valueOrLength, context) {
        return typeof valueOrLength == 'number'
            ? field(new ArrayType(type, valueOrLength), { typeName, ...context })
            : field(type, { typeName })(valueOrLength, context && 'name' in context
                ? context
                : _throw(withErrno('EINVAL', 'Invalid decorator context object')));
    }
    return __decoratePrimitiveField;
}
/**
 * Shortcuts for primitive types
 * Instead of writing `@field(primitive.types[primitive.normalize(<type>)])`, you can write `@types.type`.
 * You can also use `@types.type(length)` for arrays.
 */
export const types = Object.fromEntries(primitive.validNames.map(t => [t, _shortcut(t)]));
/**
 * Due to restrictions on decorators, `@struct` can not narrow the type of the class.
 * You must use the function to wrap the base class in the `extends` clause to force the correct type.
 */
export function $from(t) {
    return t;
}
/**
 * Like `$from`, but also adds overloads for TypedArray constructors.
 */
$from.typed = function (t) {
    return t;
};
